import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import toast from 'react-hot-toast';
import AppShell from './AppShell';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v2`;

function ReportsPage() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [reviewData, setReviewData] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [reportSettings, setReportSettings] = useState(null);
  const [scheduledReports, setScheduledReports] = useState([]);
  const [emailHistory, setEmailHistory] = useState([]);
  const [showEmailModal, setShowEmailModal] = useState(null); // 'ik' or 'iso'
  const [showScheduleModal, setShowScheduleModal] = useState(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [permissions, setPermissions] = useState(null);

  useEffect(() => {
    fetchAllData();
    fetchPermissions();
  }, []);

  const fetchPermissions = async () => {
    try {
      const token = localStorage.getItem('systemklart_token');
      const response = await axios.get(`${API}/ops/users/me/permissions`, { headers: { Authorization: `Bearer ${token}` } });
      setPermissions(response.data);
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
    }
  };

  const fetchAllData = async () => {
    try {
      const token = localStorage.getItem('systemklart_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [reviewRes, settingsRes, scheduledRes, historyRes] = await Promise.all([
        axios.get(`${API}/ops/reports/review-data`, { headers }),
        axios.get(`${API}/ops/reports/settings`, { headers }),
        axios.get(`${API}/ops/reports/scheduled`, { headers }),
        axios.get(`${API}/ops/reports/email-history`, { headers })
      ]);
      
      setReviewData(reviewRes.data);
      setReportSettings(settingsRes.data);
      setScheduledReports(scheduledRes.data.scheduled_reports || []);
      setEmailHistory(historyRes.data.history || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error(t('reports.errors.couldNotFetch'));
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = async (reportType) => {
    setGenerating(true);
    try {
      const token = localStorage.getItem('systemklart_token');
      const endpoint = reportType === 'ik' 
        ? `${API}/ops/export/ik-annual-review`
        : `${API}/ops/export/iso-management-review`;
      
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = reportType === 'ik' 
        ? `ik-arlig-gjennomgang-${new Date().getFullYear()}.pdf`
        : `iso-ledelsens-gjennomgang-Q${Math.ceil((new Date().getMonth() + 1) / 3)}-${new Date().getFullYear()}.pdf`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(t('reports.reportDownloaded'));
    } catch (error) {
      console.error('Report generation failed:', error);
      toast.error('Kunne ikke generere rapport');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-cyan-500 border-t-transparent"></div>
      </div>
    );
  }

  const summary = reviewData?.summary || {};
  const taskStats = summary.tasks || {};
  const deviationStats = summary.deviations || {};

  return (
    <AppShell permissions={permissions}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white">{t('reports.title')}</h1>
            <p className="text-sm text-slate-400 mt-1">
              {t('reports.subtitle')}
            </p>
          </div>
          <button
            onClick={() => setShowSettingsModal(true)}
            className="flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:bg-slate-800 rounded-lg transition-colors border border-slate-700"
            data-testid="report-settings-btn"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            {t('settings.title')}
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {[
            { key: 'overview', label: t('reports.overview') },
            { key: 'ik', label: t('reports.ikAnnual') },
            { key: 'iso', label: t('reports.isoReview') },
            { key: 'scheduled', label: `${t('reports.scheduled')} (${scheduledReports.length})` }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === tab.key
                  ? 'bg-cyan-500 text-slate-950 shadow-lg shadow-cyan-500/25'
                  : 'bg-slate-800/50 text-slate-400 hover:text-white border border-slate-700/50'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <StatCard
                title={t('reports.stats.tasksCompleted')}
                value={`${taskStats.completed || 0}/${taskStats.total || 0}`}
                percent={taskStats.completion_percent || 0}
                color="blue"
              />
              <StatCard
                title={t('reports.stats.deviationsClosed')}
                value={`${deviationStats.closed || 0}/${deviationStats.total || 0}`}
                percent={deviationStats.closure_percent || 100}
                color="green"
              />
              <StatCard
                title={t('reports.stats.inProgress')}
                value={taskStats.in_progress || 0}
                subtitle={t('reports.stats.tasks')}
                color="amber"
              />
              <StatCard
                title={t('reports.stats.openDeviations')}
                value={deviationStats.open || 0}
                subtitle={deviationStats.high_severity > 0 ? `${deviationStats.high_severity} ${t('reports.stats.critical')}` : ''}
                color={deviationStats.high_severity > 0 ? 'red' : 'gray'}
              />
            </div>

            {/* Report Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <ReportCard
                title={t('reports.ikAnnual')}
                description={t('reports.ikDescription')}
                icon={
                  <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                }
                onDownload={() => downloadReport('ik')}
                onEmail={() => setShowEmailModal('ik_annual')}
                onSchedule={() => setShowScheduleModal('ik_annual')}
                generating={generating}
                features={[
                  t('reports.ikFeatures.ikStatus'),
                  t('reports.ikFeatures.deviationOverview'),
                  t('reports.ikFeatures.taskStatusPerPhase'),
                  t('reports.ikFeatures.actionsNextPeriod')
                ]}
                recipientCount={reportSettings?.ik_recipients?.length || 0}
                t={t}
              />
              
              <ReportCard
                title={t('reports.isoReview')}
                description={t('reports.isoDescription')}
                icon={
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                }
                onDownload={() => downloadReport('iso')}
                onEmail={() => setShowEmailModal('iso_management')}
                onSchedule={() => setShowScheduleModal('iso_management')}
                generating={generating}
                features={[
                  t('reports.isoFeatures.auditStatus'),
                  t('reports.isoFeatures.goalsAchievement'),
                  t('reports.isoFeatures.deviationsActions'),
                  t('reports.isoFeatures.improvementOpportunities')
                ]}
                recipientCount={reportSettings?.iso_recipients?.length || 0}
                t={t}
              />
            </div>

            {/* Recent Email History */}
            {emailHistory.length > 0 && (
              <div className="bg-slate-900/60 backdrop-blur-sm border border-slate-800/60 rounded-2xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4">{t('reports.recentlySent')}</h3>
                <div className="space-y-3">
                  {emailHistory.slice(0, 5).map((item, i) => (
                    <div key={i} className="flex items-center justify-between py-2 border-b border-slate-800/50 last:border-0">
                      <div>
                        <p className="text-sm font-medium text-slate-200">
                          {item.report_type === 'ik_annual' ? t('reports.ikAnnual') : t('reports.isoReview')}
                        </p>
                        <p className="text-xs text-slate-500">
                          {t('reports.sentTo')} {item.recipients?.length || 0} {t('reports.recipients')} {t('reports.by')} {item.sent_by}
                        </p>
                      </div>
                      <span className="text-xs text-slate-500">
                        {new Date(item.sent_at).toLocaleDateString('nb-NO')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* IK Tab */}
        {activeTab === 'ik' && (
          <IKReviewSection 
            reviewData={reviewData} 
            onDownload={() => downloadReport('ik')} 
            onEmail={() => setShowEmailModal('ik_annual')}
            generating={generating} 
          />
        )}

        {/* ISO Tab */}
        {activeTab === 'iso' && (
          <ISOReviewSection 
            reviewData={reviewData} 
            onDownload={() => downloadReport('iso')} 
            onEmail={() => setShowEmailModal('iso_management')}
            generating={generating} 
          />
        )}

        {/* Scheduled Tab */}
        {activeTab === 'scheduled' && (
          <ScheduledReportsSection 
            scheduledReports={scheduledReports}
            onRefresh={fetchAllData}
          />
        )}
      </div>

      {/* Email Modal */}
      {showEmailModal && (
        <SendEmailModal
          reportType={showEmailModal}
          defaultRecipients={showEmailModal === 'ik_annual' ? reportSettings?.ik_recipients : reportSettings?.iso_recipients}
          onClose={() => setShowEmailModal(null)}
          onSent={fetchAllData}
        />
      )}

      {/* Schedule Modal */}
      {showScheduleModal && (
        <ScheduleReportModal
          reportType={showScheduleModal}
          defaultRecipients={showScheduleModal === 'ik_annual' ? reportSettings?.ik_recipients : reportSettings?.iso_recipients}
          onClose={() => setShowScheduleModal(null)}
          onScheduled={fetchAllData}
        />
      )}

      {/* Settings Modal */}
      {showSettingsModal && (
        <ReportSettingsModal
          settings={reportSettings}
          onClose={() => setShowSettingsModal(false)}
          onSaved={(newSettings) => {
            setReportSettings(newSettings);
            setShowSettingsModal(false);
          }}
        />
      )}
    </AppShell>
  );
}

function StatCard({ title, value, percent, subtitle, color }) {
  const colorClasses = {
    blue: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
    green: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    amber: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
    red: 'bg-rose-500/10 text-rose-400 border-rose-500/20',
    gray: 'bg-slate-800/50 text-slate-400 border-slate-700/50'
  };
  
  return (
    <div className={`rounded-2xl p-5 border ${colorClasses[color] || colorClasses.gray}`}>
      <p className="text-sm font-medium text-slate-400">{title}</p>
      <p className="text-3xl font-bold mt-1">{value}</p>
      {percent !== undefined && (
        <div className="mt-2">
          <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
            <div 
              className="h-full bg-current opacity-60 rounded-full transition-all"
              style={{ width: `${percent}%` }}
            />
          </div>
        </div>
      )}
      {subtitle && <p className="text-xs mt-1 text-slate-500">{subtitle}</p>}
    </div>
  );
}

function ReportCard({ title, description, icon, onDownload, onEmail, onSchedule, generating, features, recipientCount, t }) {
  return (
    <div className="bg-slate-900/60 backdrop-blur-sm border border-slate-800/60 rounded-2xl p-6 hover:border-slate-700 transition-all">
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 w-12 h-12 bg-slate-800/50 rounded-xl flex items-center justify-center">
          {icon}
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-white">{title}</h3>
          <p className="text-sm text-slate-400 mt-1">{description}</p>
          
          {features && (
            <ul className="mt-3 space-y-1">
              {features.map((feature, i) => (
                <li key={i} className="text-sm text-slate-400 flex items-center gap-2">
                  <svg className="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {feature}
                </li>
              ))}
            </ul>
          )}
          
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              onClick={onDownload}
              disabled={generating}
              className="px-4 py-2 bg-cyan-500 text-slate-950 rounded-lg text-sm font-semibold hover:bg-cyan-400 transition-colors disabled:opacity-50 flex items-center gap-2 shadow-lg shadow-cyan-500/25"
              data-testid="download-report-btn"
            >
              {generating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-950"></div>
                  {t('common.generating')}
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  {t('common.download')}
                </>
              )}
            </button>
            
            <button
              onClick={onEmail}
              className="px-4 py-2 bg-emerald-500 text-slate-950 rounded-lg text-sm font-semibold hover:bg-emerald-400 transition-colors flex items-center gap-2 shadow-lg shadow-emerald-500/25"
              data-testid="send-email-btn"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              {t('reports.sendEmail')}
              {recipientCount > 0 && (
                <span className="bg-emerald-400 text-xs px-1.5 rounded">{recipientCount}</span>
              )}
            </button>
            
            <button
              onClick={onSchedule}
              className="px-4 py-2 bg-slate-800/50 text-slate-300 rounded-lg text-sm font-medium hover:bg-slate-700 transition-colors flex items-center gap-2 border border-slate-700"
              data-testid="schedule-report-btn"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              {t('reports.schedule')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SendEmailModal({ reportType, defaultRecipients, onClose, onSent }) {
  const { t } = useTranslation();
  const [recipients, setRecipients] = useState(defaultRecipients || []);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const addRecipient = () => {
    if (newEmail && newEmail.includes('@')) {
      setRecipients([...recipients, { email: newEmail, name: newName }]);
      setNewEmail('');
      setNewName('');
    }
  };

  const removeRecipient = (index) => {
    setRecipients(recipients.filter((_, i) => i !== index));
  };

  const handleSend = async () => {
    if (recipients.length === 0) {
      toast.error(t('reports.modal.addAtLeastOne'));
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem('systemklart_token');
      const response = await axios.post(`${API}/ops/reports/send`, {
        report_type: reportType,
        recipients,
        additional_message: message || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success(`${t('reports.modal.reportSentTo')} ${response.data.sent_count} ${t('reports.recipients')}`);
        onSent();
        onClose();
      } else {
        toast.error(t('email.someEmailsFailed'));
      }
    } catch (error) {
      console.error('Failed to send report:', error);
      toast.error(t('reports.errors.couldNotSend'));
    } finally {
      setSending(false);
    }
  };

  const reportTitle = reportType === 'ik_annual' ? t('reports.ikAnnual') : t('reports.isoReview');

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b">
          <h2 className="text-xl font-bold text-white">{t('reports.modal.send')} {reportTitle}</h2>
          <p className="text-sm text-slate-400 mt-1">{t('reports.modal.selectRecipients')}</p>
        </div>

        <div className="p-6 space-y-4">
          {/* Recipients List */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">{t('email.recipients')}</label>
            <div className="space-y-2 mb-3">
              {recipients.map((r, i) => (
                <div key={i} className="flex items-center justify-between bg-slate-800 rounded-lg px-3 py-2">
                  <div>
                    <p className="text-sm font-medium text-white">{r.name || r.email}</p>
                    {r.name && <p className="text-xs text-slate-400">{r.email}</p>}
                  </div>
                  <button onClick={() => removeRecipient(i)} className="text-red-500 hover:text-red-700">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>

            {/* Add Recipient */}
            <div className="flex gap-2">
              <input
                type="text"
                placeholder={t('email.name')}
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                className="flex-1 px-3 py-2 border rounded-lg text-sm"
              />
              <input
                type="email"
                placeholder={`${t('email.emailAddress')} *`}
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                className="flex-1 px-3 py-2 border rounded-lg text-sm"
              />
              <button
                onClick={addRecipient}
                className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-200"
              >
                {t('email.addRecipient')}
              </button>
            </div>
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">{t('email.message')} ({t('common.optional')})</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t('email.messagePlaceholder')}
              rows={3}
              className="w-full px-3 py-2 border rounded-lg text-sm resize-none"
            />
          </div>
        </div>

        <div className="p-6 border-t bg-slate-800 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-slate-300 hover:bg-gray-200 rounded-lg text-sm font-medium">
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSend}
            disabled={sending || recipients.length === 0}
            className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
          >
            {sending ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                {t('email.sending')}
              </>
            ) : (
              <>{t('email.sendReport')}</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function ScheduleReportModal({ reportType, defaultRecipients, onClose, onScheduled }) {
  const { t } = useTranslation();
  const [recipients, setRecipients] = useState(defaultRecipients || []);
  const [newEmail, setNewEmail] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [recurrence, setRecurrence] = useState('');
  const [scheduling, setScheduling] = useState(false);

  const addRecipient = () => {
    if (newEmail && newEmail.includes('@')) {
      setRecipients([...recipients, { email: newEmail }]);
      setNewEmail('');
    }
  };

  const handleSchedule = async () => {
    if (recipients.length === 0 || !scheduledDate) {
      toast.error(t('reports.modal.selectDateAndRecipient'));
      return;
    }

    setScheduling(true);
    try {
      const token = localStorage.getItem('systemklart_token');
      await axios.post(`${API}/ops/reports/schedule`, {
        report_type: reportType,
        recipients,
        scheduled_date: new Date(scheduledDate).toISOString(),
        recurrence: recurrence || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(t('reports.modal.reportScheduled'));
      onScheduled();
      onClose();
    } catch (error) {
      console.error('Failed to schedule report:', error);
      toast.error(t('reports.errors.couldNotSchedule'));
    } finally {
      setScheduling(false);
    }
  };

  const reportTitle = reportType === 'ik_annual' ? t('reports.ikAnnual') : t('reports.isoReview');

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 rounded-xl max-w-lg w-full">
        <div className="p-6 border-b">
          <h2 className="text-xl font-bold text-white">{t('reports.schedule')} {reportTitle}</h2>
        </div>

        <div className="p-6 space-y-4">
          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">{t('reports.modal.sendDate')}</label>
            <input
              type="date"
              value={scheduledDate}
              onChange={(e) => setScheduledDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className="w-full px-3 py-2 border rounded-lg text-sm"
            />
          </div>

          {/* Recurrence */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">{t('reports.modal.recurrence')}</label>
            <select
              value={recurrence}
              onChange={(e) => setRecurrence(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm"
            >
              <option value="">{t('reports.modal.noRecurrence')}</option>
              <option value="yearly">{t('reports.modal.yearly')}</option>
              <option value="quarterly">{t('reports.modal.quarterly')}</option>
            </select>
          </div>

          {/* Recipients */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">{t('email.recipients')} ({recipients.length})</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {recipients.map((r, i) => (
                <span key={i} className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs">
                  {r.email}
                  <button onClick={() => setRecipients(recipients.filter((_, idx) => idx !== i))} className="hover:text-red-600">×</button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="email"
                placeholder={t('email.emailAddress')}
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                className="flex-1 px-3 py-2 border rounded-lg text-sm"
              />
              <button onClick={addRecipient} className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg text-sm">
                {t('email.addRecipient')}
              </button>
            </div>
          </div>
        </div>

        <div className="p-6 border-t bg-slate-800 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-slate-300 hover:bg-gray-200 rounded-lg text-sm font-medium">
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSchedule}
            disabled={scheduling}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            {scheduling ? t('reports.modal.scheduling') : t('reports.schedule')}
          </button>
        </div>
      </div>
    </div>
  );
}

function ReportSettingsModal({ settings, onClose, onSaved }) {
  const { t } = useTranslation();
  const [ikRecipients, setIkRecipients] = useState(settings?.ik_recipients || []);
  const [isoRecipients, setIsoRecipients] = useState(settings?.iso_recipients || []);
  const [newIkEmail, setNewIkEmail] = useState('');
  const [newIkName, setNewIkName] = useState('');
  const [newIkRole, setNewIkRole] = useState('');
  const [newIsoEmail, setNewIsoEmail] = useState('');
  const [newIsoName, setNewIsoName] = useState('');
  const [newIsoRole, setNewIsoRole] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('systemklart_token');
      const response = await axios.put(`${API}/ops/reports/settings`, {
        ik_recipients: ikRecipients,
        iso_recipients: isoRecipients
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(t('reports.settings.saved'));
      onSaved(response.data.settings);
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error(t('reports.settings.couldNotSave'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b">
          <h2 className="text-xl font-bold text-white">{t('reports.settings.title')}</h2>
          <p className="text-sm text-slate-400 mt-1">{t('reports.settings.description')}</p>
        </div>

        <div className="p-6 space-y-6">
          {/* IK Recipients */}
          <div>
            <h3 className="text-sm font-semibold text-white mb-3">{t('reports.ikAnnual')} - {t('email.recipients')}</h3>
            <div className="space-y-2 mb-3">
              {ikRecipients.map((r, i) => (
                <div key={i} className="flex items-center justify-between bg-blue-50 rounded-lg px-3 py-2">
                  <div>
                    <p className="text-sm font-medium text-white">{r.name || r.email}</p>
                    <p className="text-xs text-slate-400">{r.email} {r.role && `• ${r.role}`}</p>
                  </div>
                  <button onClick={() => setIkRecipients(ikRecipients.filter((_, idx) => idx !== i))} className="text-red-500 hover:text-red-700 text-sm">{t('common.remove')}</button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input type="text" placeholder={t('email.name')} value={newIkName} onChange={(e) => setNewIkName(e.target.value)} className="flex-1 px-3 py-2 border rounded-lg text-sm" />
              <input type="email" placeholder={`${t('email.emailAddress')} *`} value={newIkEmail} onChange={(e) => setNewIkEmail(e.target.value)} className="flex-1 px-3 py-2 border rounded-lg text-sm" />
              <input type="text" placeholder={t('reports.settings.role')} value={newIkRole} onChange={(e) => setNewIkRole(e.target.value)} className="w-32 px-3 py-2 border rounded-lg text-sm" />
              <button
                onClick={() => {
                  if (newIkEmail.includes('@')) {
                    setIkRecipients([...ikRecipients, { email: newIkEmail, name: newIkName, role: newIkRole }]);
                    setNewIkEmail(''); setNewIkName(''); setNewIkRole('');
                  }
                }}
                className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg text-sm"
              >
                {t('email.addRecipient')}
              </button>
            </div>
          </div>

          {/* ISO Recipients */}
          <div>
            <h3 className="text-sm font-semibold text-white mb-3">{t('reports.isoReview')} - {t('email.recipients')}</h3>
            <div className="space-y-2 mb-3">
              {isoRecipients.map((r, i) => (
                <div key={i} className="flex items-center justify-between bg-green-50 rounded-lg px-3 py-2">
                  <div>
                    <p className="text-sm font-medium text-white">{r.name || r.email}</p>
                    <p className="text-xs text-slate-400">{r.email} {r.role && `• ${r.role}`}</p>
                  </div>
                  <button onClick={() => setIsoRecipients(isoRecipients.filter((_, idx) => idx !== i))} className="text-red-500 hover:text-red-700 text-sm">{t('common.remove')}</button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input type="text" placeholder={t('email.name')} value={newIsoName} onChange={(e) => setNewIsoName(e.target.value)} className="flex-1 px-3 py-2 border rounded-lg text-sm" />
              <input type="email" placeholder={`${t('email.emailAddress')} *`} value={newIsoEmail} onChange={(e) => setNewIsoEmail(e.target.value)} className="flex-1 px-3 py-2 border rounded-lg text-sm" />
              <input type="text" placeholder={t('reports.settings.role')} value={newIsoRole} onChange={(e) => setNewIsoRole(e.target.value)} className="w-32 px-3 py-2 border rounded-lg text-sm" />
              <button
                onClick={() => {
                  if (newIsoEmail.includes('@')) {
                    setIsoRecipients([...isoRecipients, { email: newIsoEmail, name: newIsoName, role: newIsoRole }]);
                    setNewIsoEmail(''); setNewIsoName(''); setNewIsoRole('');
                  }
                }}
                className="px-3 py-2 bg-green-100 text-green-700 rounded-lg text-sm"
              >
                {t('email.addRecipient')}
              </button>
            </div>
          </div>
        </div>

        <div className="p-6 border-t bg-slate-800 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-slate-300 hover:bg-gray-200 rounded-lg text-sm font-medium">{t('common.cancel')}</button>
          <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
            {saving ? t('reports.settings.saving') : t('reports.settings.saveSettings')}
          </button>
        </div>
      </div>
    </div>
  );
}

function ScheduledReportsSection({ scheduledReports, onRefresh }) {
  const { t } = useTranslation();
  
  const cancelReport = async (reportId) => {
    try {
      const token = localStorage.getItem('systemklart_token');
      await axios.delete(`${API}/ops/reports/schedule/${reportId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('reports.scheduledSection.cancelled'));
      onRefresh();
    } catch (error) {
      toast.error(t('reports.scheduledSection.couldNotCancel'));
    }
  };

  if (scheduledReports.length === 0) {
    return (
      <div className="bg-slate-900 rounded-xl border p-12 text-center">
        <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        <h3 className="text-lg font-medium text-white mb-1">{t('reports.scheduledSection.noScheduled')}</h3>
        <p className="text-sm text-slate-400">{t('reports.scheduledSection.goToOverview')}</p>
      </div>
    );
  }

  return (
    <div className="bg-slate-900 rounded-xl border">
      <div className="p-6 border-b">
        <h2 className="text-lg font-semibold text-white">{t('reports.scheduledSection.title')}</h2>
      </div>
      <div className="divide-y">
        {scheduledReports.map(report => (
          <div key={report.id} className="p-4 flex items-center justify-between hover:bg-slate-800">
            <div>
              <p className="font-medium text-white">
                {report.report_type === 'ik_annual' ? t('reports.ikAnnual') : t('reports.isoReview')}
              </p>
              <p className="text-sm text-slate-400">
                {t('reports.scheduledSection.sendsOn')}: {new Date(report.scheduled_date).toLocaleDateString('nb-NO')}
                {report.recurrence && ` • ${report.recurrence === 'yearly' ? t('reports.modal.yearly') : t('reports.modal.quarterly')}`}
              </p>
              <p className="text-xs text-gray-400">{report.recipients?.length || 0} {t('reports.recipients')}</p>
            </div>
            <button
              onClick={() => cancelReport(report.id)}
              className="px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium"
            >
              {t('common.cancel')}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function IKReviewSection({ reviewData, onDownload, onEmail, generating }) {
  const { t } = useTranslation();
  const taskStats = reviewData?.summary?.tasks || {};
  const deviationStats = reviewData?.summary?.deviations || {};
  
  const ikRequirements = [
    { id: '§5.1', section: 1, completed: true },
    { id: '§5.2', section: 2, completed: taskStats.completion_percent > 30 },
    { id: '§5.3', section: 3, completed: true },
    { id: '§5.4', section: 4, completed: taskStats.completion_percent > 20 },
    { id: '§5.5', section: 5, completed: taskStats.completion_percent > 10 },
    { id: '§5.6', section: 6, completed: taskStats.completion_percent > 40 },
    { id: '§5.7', section: 7, completed: true },
    { id: '§5.8', section: 8, completed: true },
  ];
  
  const completedReqs = ikRequirements.filter(r => r.completed).length;
  
  return (
    <div className="space-y-6">
      <div className="bg-slate-900 rounded-xl border p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-white">{t('reports.ikAnnual')}</h2>
            <p className="text-sm text-slate-400 mt-1">{t('reports.ikLegalRef')}</p>
          </div>
          <div className="flex gap-2">
            <button onClick={onEmail} className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700">{t('reports.sendEmail')}</button>
            <button onClick={onDownload} disabled={generating} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
              {generating ? t('common.generating') : t('common.download')}
            </button>
          </div>
        </div>
        
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-blue-50 rounded-lg p-4 text-center">
            <p className="text-2xl font-bold text-blue-700">{completedReqs}/8</p>
            <p className="text-sm text-blue-600">{t('reports.ikSection.requirementsFulfilled')}</p>
          </div>
          <div className="bg-green-50 rounded-lg p-4 text-center">
            <p className="text-2xl font-bold text-green-700">{taskStats.completion_percent || 0}%</p>
            <p className="text-sm text-green-600">{t('reports.stats.tasksCompleted')}</p>
          </div>
          <div className="bg-amber-50 rounded-lg p-4 text-center">
            <p className="text-2xl font-bold text-amber-700">{deviationStats.closure_percent || 100}%</p>
            <p className="text-sm text-amber-600">{t('reports.stats.deviationsClosed')}</p>
          </div>
        </div>
        
        <h3 className="font-semibold text-white mb-3">{t('reports.ikRequirements')}</h3>
        <p className="text-sm text-slate-400 mb-2">{t('reports.clickToSeeTask')}</p>
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-800">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">{t('reports.table.requirement')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">{t('reports.table.description')}</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase">{t('reports.table.status')}</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {ikRequirements.map((req) => (
                <tr key={req.id} className="hover:bg-blue-50 cursor-pointer transition-colors" data-testid={`ik-req-row-${req.section}`}>
                  <td className="px-4 py-3 text-sm font-medium text-white">{req.id}</td>
                  <td className="px-4 py-3 text-sm text-slate-400">{t(`tasks.ikSection.${req.section}`)}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${req.completed ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
                      {req.completed ? t('reports.status.fulfilled') : t('reports.status.partial')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Link 
                      to={`/tasks?ik_section=${req.section}`}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      data-testid={`ik-req-link-${req.section}`}
                    >
                      {t('reports.seeTasks')} →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ISOReviewSection({ reviewData, onDownload, onEmail, generating }) {
  const { t } = useTranslation();
  const taskStats = reviewData?.summary?.tasks || {};
  const deviationStats = reviewData?.summary?.deviations || {};
  
  const reviewInputs = [
    { key: 'previousActions', status: 'ok' },
    { key: 'internalExternalChanges', status: 'ok' },
    { key: 'monitoringResults', status: 'partial' },
    { key: 'auditResults', status: taskStats.completion_percent > 50 ? 'ok' : 'partial' },
    { key: 'stakeholderFeedback', status: 'ok' },
    { key: 'supplierPerformance', status: 'partial' },
    { key: 'resourceAdequacy', status: 'ok' },
    { key: 'riskActions', status: deviationStats.closure_percent > 70 ? 'ok' : 'partial' },
    { key: 'improvementOpportunities', status: 'ok' },
  ];
  
  return (
    <div className="space-y-6">
      <div className="bg-slate-900 rounded-xl border p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-white">{t('reports.isoReview')}</h2>
            <p className="text-sm text-slate-400 mt-1">{t('reports.isoLegalRef')}</p>
          </div>
          <div className="flex gap-2">
            <button onClick={onEmail} className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700">{t('reports.sendEmail')}</button>
            <button onClick={onDownload} disabled={generating} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
              {generating ? t('common.generating') : t('common.download')}
            </button>
          </div>
        </div>
        
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-green-50 rounded-lg p-4 text-center">
            <p className="text-2xl font-bold text-green-700">{taskStats.completed || 0}</p>
            <p className="text-sm text-green-600">{t('reports.isoSection.isoTasksCompleted')}</p>
          </div>
          <div className="bg-blue-50 rounded-lg p-4 text-center">
            <p className="text-2xl font-bold text-blue-700">{deviationStats.total || 0}</p>
            <p className="text-sm text-blue-600">{t('reports.isoSection.registeredDeviations')}</p>
          </div>
          <div className="bg-purple-50 rounded-lg p-4 text-center">
            <p className="text-2xl font-bold text-purple-700">Q{Math.ceil((new Date().getMonth() + 1) / 3)}</p>
            <p className="text-sm text-purple-600">{new Date().getFullYear()}</p>
          </div>
        </div>
        
        <h3 className="font-semibold text-white mb-3">{t('reports.isoSection.inputTitle')}</h3>
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-800">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">{t('reports.isoSection.inputElement')}</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase">{t('reports.table.status')}</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {reviewInputs.map((input, i) => (
                <tr key={i} className="hover:bg-slate-800">
                  <td className="px-4 py-3 text-sm text-slate-400">{t(`reports.isoInputs.${input.key}`)}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${input.status === 'ok' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
                      {input.status === 'ok' ? t('reports.status.available') : t('reports.status.partial')}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default ReportsPage;
